import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { useAuth } from '../contexts/AuthContext';
const VerifyEmailPendingPage = () => {
    const [message, setMessage] = useState(null);
    const [cooldown, setCooldown] = useState(0);
    const [isResending, setIsResending] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const email = location.state?.email || user?.email;
    // If we don't have an email, redirect to login
    useEffect(() => {
        if (!email) {
            navigate('/login');
        }
    }, [email, navigate]);
    useEffect(() => {
        let timer;
        if (cooldown > 0) {
            timer = setInterval(() => {
                setCooldown((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [cooldown]);
    const handleResend = async () => {
        if (cooldown > 0 || isResending)
            return;
        setIsResending(true);
        setMessage(null);
        try {
            await apiClient.post('/auth/resend-verification', { email });
            setMessage({ text: 'Verification email resent. Please check your inbox.', type: 'success' });
            setCooldown(60);
        }
        catch (error) {
            const resData = error.response?.data;
            if (error.response?.status === 429) {
                setMessage({ text: resData?.message || 'Please wait a bit before requesting another email.', type: 'error' });
                setCooldown(60);
            }
            else {
                setMessage({ text: resData?.message || 'Failed to resend verification email. Please try again later.', type: 'error' });
            }
        }
        finally {
            setIsResending(false);
        }
    };
    if (!email)
        return null;
    return (_jsx("div", { className: "min-h-screen flex items-center justify-center px-4 bg-bg-primary", children: _jsx("div", { className: "max-w-md w-full", children: _jsxs("div", { className: "bg-bg-secondary border border-border-color rounded-2xl p-8 shadow-2xl text-center transform transition-all", children: [_jsx("div", { className: "mb-6 inline-flex items-center justify-center w-20 h-20 rounded-full bg-brand-primary bg-opacity-10 text-brand-primary", children: _jsx("svg", { className: "w-10 h-10", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 1.5, d: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" }) }) }), _jsx("h1", { className: "font-display text-3xl font-bold text-text-primary mb-4", children: "Check your inbox" }), _jsxs("p", { className: "text-text-secondary mb-8 leading-relaxed", children: ["We've sent a verification link to ", _jsx("span", { className: "text-text-primary font-medium", children: email }), ". Once confirmed, you'll be able to access your account."] }), _jsxs("div", { className: "space-y-4", children: [_jsxs("button", { onClick: handleResend, disabled: cooldown > 0 || isResending, className: `w-full py-3.5 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center space-x-2
                ${cooldown > 0 || isResending
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                    : 'bg-brand-primary hover:bg-brand-hover text-white shadow-lg shadow-brand-primary/20 hover:scale-[1.02] active:scale-[0.98]'}`, children: [isResending ? (_jsx("span", { className: "inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" })) : null, _jsx("span", { children: cooldown > 0 ? `Resend available in ${cooldown}s` : 'Resend Verification Email' })] }), message && (_jsx("div", { className: `mt-6 p-4 rounded-xl text-sm border animate-in fade-in slide-in-from-top-2 duration-300
                ${message.type === 'success'
                                    ? 'bg-green-500/10 border-green-500/20 text-green-400'
                                    : 'bg-red-500/10 border-red-500/20 text-red-400'}`, children: message.text }))] }), _jsx("div", { className: "mt-10 pt-8 border-t border-border-color", children: _jsx(Link, { to: "/login", className: "text-text-secondary hover:text-brand-primary font-medium transition-colors duration-200", children: "Back to Sign In" }) })] }) }) }));
};
export default VerifyEmailPendingPage;
